import { useCallback, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';

const compareSearchStrings = (
	oldSearch = '',
	newSearch = '',
	ignoredTags = ['preserve', 'backlinkParams', 'backLinkText', 'pathName']
) => {
	let oldParams = new URLSearchParams(oldSearch);
	let newParams = new URLSearchParams(newSearch);
	ignoredTags.forEach((tag) => {
		oldParams.delete(tag);
		newParams.delete(tag);
	});
	return oldParams.toString() === newParams.toString();
};
const changeInCurrentPagePagination = (oldSearch, newSearch) => {
	const oldParams = new URLSearchParams(oldSearch);
	const newParams = new URLSearchParams(newSearch);
	const oldPage = oldParams.get('current') || 1;
	const newPage = newParams.get('current') || 1;
	return oldPage !== newPage;
};

/*
Conditions:
	1. Path Change
		a. Path Change exists with these filters, if so: reset to previous scroll position
		b. Path Change exists, but not with these filters: reset to 0
	2. Pagination current page changes: reset to 0
	3. No Path Change, but filter change: do nothing
 */

function ScrollManager({ history, redirect, layout }) {
	const { location } = history;
	const prevKeyRef = useRef(location.pathname);
	const prevSearch = useRef(location.search);
	const mainContainer = layout && layout.current;
	const scrollContainer = redirect && redirect.current;
	const currentPage =
		location && location.search && new URLSearchParams(location.search).get('current');

	const scrollTop = nullSafeGetOrElse('scrollTop', scrollContainer, 0);

	const scrollPage = useCallback(
		(y, behavior = 'instant') => {
			let timeoutId: NodeJS.Timeout;
			let attempts = 0;
			const maxAttempts = 100; // ~5 seconds
			if (scrollContainer && mainContainer) {
				const waitForHeightAndScroll = () => {
					if (mainContainer.offsetHeight >= y + scrollContainer.offsetHeight) {
						scrollContainer.scrollTo({
							top: y,
							behavior,
						});
					} else if (attempts < maxAttempts) {
						attempts++;
						timeoutId = setTimeout(waitForHeightAndScroll, 50);
					}
				};
				waitForHeightAndScroll();

				return () => {
					clearTimeout(timeoutId);
				};
			}
		},
		[scrollContainer, mainContainer]
	);

	//Logic for when we load a page here:
	useEffect(() => {
		if (!mainContainer || !scrollContainer) return;
		const lastPath = prevKeyRef.current;
		const currentPath = location.pathname;
		const savedSearch = sessionStorage.getItem(`filters:${location.pathname}`);
		const lastSearch = prevSearch.current;
		const currentSearch = location.search;

		//1. Path Change
		if (lastPath !== currentPath) {
			//As we leave the page, we store the old scroll position
			sessionStorage.setItem(`scrollY:${lastPath}`, scrollTop);

			//a. Path Change exists with these filters,
			if (compareSearchStrings(savedSearch, currentSearch)) {
				const savedY = parseInt(sessionStorage.getItem(`scrollY:${currentPath}`) || '0');
				scrollPage(savedY, 'instant');

				//b. Path Change exists, but not with these filters
			} else {
				sessionStorage.setItem(`scrollY:${currentPath}`, scrollTop);
				scrollPage(0, 'instant');
			}
		} else {
			//2. No Path Change, but current page change (pagination)
			if (changeInCurrentPagePagination(currentSearch, lastSearch)) {
				sessionStorage.setItem(`scrollY:${currentPath}`, scrollTop);
				scrollPage(0, 'instant');
			}

			//3. No Path Change, but filter change
			sessionStorage.setItem(`filters:${currentPath}`, currentSearch);
		}

		prevKeyRef.current = location.pathname;
		prevSearch.current = location.search;
	}, [location.pathname, location.search, currentPage]);

	return null;
}

export default withRouter(ScrollManager);
